import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase";
import logo from "../assets/river-ranch-logo.png";
import hero from "../assets/hero-poker.jpg";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState("");
  const [resetError, setResetError] = useState("");

  const navigate = useNavigate();

  const sendCustomPasswordSetupEmail = httpsCallable(
    functions,
    "sendCustomPasswordSetupEmail"
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const userCredential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        password
      );

      let mustChangePassword = false;

      try {
        const userRef = doc(db, "users", userCredential.user.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data() || {};
          mustChangePassword = userData.mustChangePassword === true;
        }

        await updateDoc(userRef, {
          lastLoginAt: serverTimestamp(),
        });
      } catch (firestoreError) {
        console.error("User doc read/update failed during login:", {
          code: firestoreError?.code,
          message: firestoreError?.message,
          uid: userCredential?.user?.uid,
        });
      }

      const params = new URLSearchParams(window.location.search);
      const redirectParam = params.get("redirect");
      const redirect = redirectParam ? decodeURIComponent(redirectParam) : "/dashboard";

      if (mustChangePassword) {
        navigate(
          `/force-password-change?redirect=${encodeURIComponent(redirect)}`,
          { replace: true }
        );
      } else {
        navigate(redirect || "/dashboard", { replace: true });
      }
    } catch (error) {
      console.error("Login failed:", error);
      alert("Invalid login credentials");
    }

    setLoading(false);
  };

  const openResetPanel = () => {
    setShowReset((prev) => !prev);
    setResetMessage("");
    setResetError("");

    if (!resetEmail && email.trim()) {
      setResetEmail(email.trim());
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    const normalizedEmail = resetEmail.trim().toLowerCase();

    setResetLoading(true);
    setResetMessage("");
    setResetError("");

    if (!normalizedEmail) {
      setResetError("Please enter your email address.");
      setResetLoading(false);
      return;
    }

    try {
      await sendCustomPasswordSetupEmail({
        email: normalizedEmail,
      });

      setResetMessage(
        "If that email belongs to an approved member account, a temporary password email has been sent from admin@riverranchpoker.club. Use that temporary password to log in, and you will be prompted to create your own password immediately."
      );
    } catch (error) {
      console.error("Temporary password email failed:", error);
      setResetError(
        error?.message ||
          "Unable to send a temporary password email right now. Please try again."
      );
    }

    setResetLoading(false);
  };

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-black text-white overflow-hidden px-6">
      <img
        src={hero}
        alt="Poker table"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />

      <div className="absolute inset-0 bg-black/70" />

      <div className="absolute inset-0 opacity-30">
        <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full blur-3xl bg-[#B87333]/20" />
        <div className="absolute -bottom-52 -right-52 h-[620px] w-[620px] rounded-full blur-3xl bg-[#B87333]/15" />
      </div>

      <form
        onSubmit={handleLogin}
        className="relative z-10 bg-zinc-900/95 backdrop-blur-sm p-8 rounded-lg border border-zinc-800 w-full max-w-md space-y-4"
      >
        <div className="flex items-center justify-between">
          <Link to="/" className="text-zinc-400 hover:text-zinc-200 text-sm">
            ← Back to Home
          </Link>

          <Link
            to="/apply"
            className="text-sm font-semibold text-zinc-950 px-3 py-2 rounded bg-[#B87333] hover:bg-[#A46230]"
          >
            Apply
          </Link>
        </div>

        <img
          src={logo}
          alt="River Ranch Poker Club"
          className="h-34 mb-2 mx-auto"
        />

        <h2 className="text-2xl font-semibold text-[#B87333] text-center">
          Membership Login
        </h2>

        <input
          type="email"
          placeholder="Email"
          className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        <input
          type="password"
          placeholder="Password"
          className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        <div className="flex justify-end">
          <button
            type="button"
            onClick={openResetPanel}
            className="text-sm text-[#B87333] hover:underline font-semibold"
          >
            Need a temporary password?
          </button>
        </div>

        {showReset ? (
          <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 p-4 space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-zinc-100">
                Email Temporary Password
              </h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Approved members can request a fresh temporary password here.
                After logging in with that temporary password, you’ll be
                required to create your own password before entering the app.
              </p>
            </div>

            <div className="space-y-2">
              <input
                type="email"
                placeholder="Enter your approved member email"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
              />

              <button
                type="button"
                onClick={handleResetPassword}
                className="w-full py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold rounded border border-zinc-700"
                disabled={resetLoading}
              >
                {resetLoading ? "Sending..." : "Email Temporary Password"}
              </button>
            </div>

            {resetMessage ? (
              <p className="text-sm text-green-400 leading-relaxed">
                {resetMessage}
              </p>
            ) : null}

            {resetError ? (
              <p className="text-sm text-red-400 leading-relaxed">
                {resetError}
              </p>
            ) : null}
          </div>
        ) : null}

        <button
          type="submit"
          className="w-full py-3 bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold rounded disabled:opacity-60"
          disabled={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        <p className="text-center text-zinc-400 text-sm">
          Not a member yet?{" "}
          <Link
            to="/apply"
            className="text-[#B87333] hover:underline font-semibold"
          >
            Apply to Join
          </Link>
        </p>
      </form>
    </div>
  );
}